import { useState } from "react";
import { Check, Edit2, Gavel, RotateCcw, Trash2, UserCheck } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import {
  useAddComment,
  useAssignEntry,
  useBacklogComments,
  useBacklogMembers,
  useCloseEntry,
  useCompleteEntry,
  useDeleteEntry,
  useReopenEntry,
  useVote,
} from "../hooks/useBacklog";
import { CATEGORY_LABELS, STATUS_LABELS } from "../types";
import type { BacklogCategory, BacklogEntry, BacklogStatus } from "../types";
import { VoteBar } from "./VoteBar";

export const CATEGORY_COLORS: Record<
  BacklogCategory,
  "blue" | "green" | "purple" | "orange" | "red" | "gray"
> = {
  contracts_sales: "blue",
  finance_investments: "green",
  people: "purple",
  processes_operations: "orange",
  legal_contract_risks: "red",
  ideas: "gray",
};

export const STATUS_COLORS: Record<BacklogStatus, "gray" | "yellow" | "green" | "black"> = {
  open: "gray",
  in_progress: "yellow",
  done: "green",
  closed: "black",
};

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

interface Props {
  entry: BacklogEntry;
  onClose: () => void;
  onEdit: (entry: BacklogEntry) => void;
}

type ActionPanel = "none" | "assign" | "close";

export function EntryDetailModal({ entry, onClose, onEdit }: Props) {
  const user = useAuthStore((s) => s.user);
  const [panel, setPanel] = useState<ActionPanel>("none");
  const [assignOwner, setAssignOwner] = useState("");
  const [decisionText, setDecisionText] = useState("");
  const [commentText, setCommentText] = useState("");

  const { data: comments, isLoading: commentsLoading } = useBacklogComments(entry.id);
  const { data: members } = useBacklogMembers();
  const voteMutation = useVote();
  const assignMutation = useAssignEntry();
  const completeMutation = useCompleteEntry();
  const closeMutation = useCloseEntry();
  const reopenMutation = useReopenEntry();
  const deleteMutation = useDeleteEntry();
  const commentMutation = useAddComment();

  const isResolved = entry.status === "done" || entry.status === "closed";
  const canDelete = user && (user.id === entry.author || user.is_ceo);
  const busy =
    assignMutation.isPending ||
    completeMutation.isPending ||
    closeMutation.isPending ||
    reopenMutation.isPending ||
    deleteMutation.isPending;

  const submitComment = () => {
    const content = commentText.trim();
    if (!content) return;
    commentMutation.mutate(
      { id: entry.id, content },
      { onSuccess: () => setCommentText("") },
    );
  };

  return (
    <Modal open onClose={onClose} title={entry.title} size="lg">
      <div className="flex flex-col gap-4">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge color={CATEGORY_COLORS[entry.category]}>
            {CATEGORY_LABELS[entry.category]}
          </Badge>
          <Badge color={STATUS_COLORS[entry.status]}>{STATUS_LABELS[entry.status]}</Badge>
          {entry.is_urgent && <Badge color="red">Pilne</Badge>}
          {entry.discussion_date && (
            <Badge color="yellow">omówić do {formatDate(entry.discussion_date)}</Badge>
          )}
          <span className="text-xs text-[var(--color-text-muted)]">
            {entry.author_name} · {formatDate(entry.created_at)}
          </span>
        </div>

        {entry.description && (
          <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap">
            {entry.description}
          </p>
        )}

        {entry.owner_name && !isResolved && (
          <p className="text-sm text-[var(--color-text-secondary)]">
            Właściciel tematu: <span className="font-medium">{entry.owner_name}</span>
          </p>
        )}

        {entry.decision && (
          <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-3">
            <p className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
              Decyzja — {entry.decided_by_name}, {formatDate(entry.decided_at)}
            </p>
            <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap">
              {entry.decision}
            </p>
          </div>
        )}

        {/* Votes */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
            Głosy
          </span>
          <VoteBar
            votes={entry.votes}
            currentUserId={user?.id ?? ""}
            onVote={isResolved ? undefined : (value) => voteMutation.mutate({ id: entry.id, value })}
            disabled={voteMutation.isPending}
          />
        </div>

        {/* Lifecycle actions */}
        <div className="flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-3">
          {!isResolved && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setPanel(panel === "assign" ? "none" : "assign");
                  setAssignOwner(entry.owner ?? user?.id ?? "");
                }}
              >
                <UserCheck size={14} className="mr-1" />
                {entry.status === "in_progress" ? "Zmień właściciela" : "Przypisz jako task"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setPanel(panel === "close" ? "none" : "close")}
              >
                <Gavel size={14} className="mr-1" />
                Zamknij z decyzją
              </Button>
            </>
          )}
          {entry.status === "in_progress" && (
            <Button
              size="sm"
              variant="primary"
              loading={completeMutation.isPending}
              onClick={() => completeMutation.mutate(entry.id)}
            >
              <Check size={14} className="mr-1" />
              Zrealizowane
            </Button>
          )}
          {isResolved && (
            <Button
              size="sm"
              variant="secondary"
              loading={reopenMutation.isPending}
              onClick={() => reopenMutation.mutate(entry.id)}
            >
              <RotateCcw size={14} className="mr-1" />
              Przywróć
            </Button>
          )}
          {!isResolved && (
            <Button size="sm" variant="ghost" onClick={() => onEdit(entry)}>
              <Edit2 size={14} className="mr-1" />
              Edytuj
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => {
                if (window.confirm("Usunąć ten wpis wraz z komentarzami i głosami?")) {
                  deleteMutation.mutate(entry.id, { onSuccess: onClose });
                }
              }}
            >
              <Trash2 size={14} className="mr-1" />
              Usuń
            </Button>
          )}
        </div>

        {panel === "assign" && (
          <div className="flex flex-col gap-3 border border-[var(--color-border)] px-4 py-3">
            <Select
              label="Właściciel tematu"
              value={assignOwner}
              onChange={(e) => setAssignOwner(e.target.value)}
              options={(members ?? []).map((m) => ({ value: m.id, label: m.name }))}
            />
            <div className="flex flex-col gap-1">
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                Decyzja (opcjonalnie)
              </label>
              <textarea
                className="input-base min-h-[60px]"
                value={decisionText}
                onChange={(e) => setDecisionText(e.target.value)}
                placeholder="np. Robimy — szukamy nowego podwykonawcy do końca sierpnia."
              />
            </div>
            <div>
              <Button
                size="sm"
                variant="primary"
                loading={assignMutation.isPending}
                disabled={!assignOwner || busy}
                onClick={() =>
                  assignMutation.mutate(
                    {
                      id: entry.id,
                      owner: assignOwner,
                      decision: decisionText.trim() || undefined,
                    },
                    {
                      onSuccess: () => {
                        setPanel("none");
                        setDecisionText("");
                      },
                    },
                  )
                }
              >
                Przypisz
              </Button>
            </div>
          </div>
        )}

        {panel === "close" && (
          <div className="flex flex-col gap-3 border border-[var(--color-border)] px-4 py-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                Decyzja i uzasadnienie (wymagane)
              </label>
              <textarea
                className="input-base min-h-[80px]"
                value={decisionText}
                onChange={(e) => setDecisionText(e.target.value)}
                placeholder="Także „odpuszczamy, bo…” — żaden temat nie umiera po cichu."
                autoFocus
              />
            </div>
            <div>
              <Button
                size="sm"
                variant="primary"
                loading={closeMutation.isPending}
                disabled={!decisionText.trim() || busy}
                onClick={() =>
                  closeMutation.mutate(
                    { id: entry.id, decision: decisionText.trim() },
                    {
                      onSuccess: () => {
                        setPanel("none");
                        setDecisionText("");
                      },
                    },
                  )
                }
              >
                Zamknij temat
              </Button>
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="border-t border-[var(--color-border)] pt-3">
          <p className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
            Dyskusja
          </p>
          {commentsLoading ? (
            <Spinner />
          ) : (
            <div className="flex flex-col gap-2">
              {(comments ?? []).map((c) => (
                <div key={c.id} className="border border-[var(--color-border)] px-3 py-2">
                  <p className="text-xs text-[var(--color-text-muted)] mb-1">
                    {c.author_name} · {formatDate(c.created_at)}
                  </p>
                  <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap">
                    {c.content}
                  </p>
                </div>
              ))}
              {(comments ?? []).length === 0 && (
                <p className="text-sm text-[var(--color-text-muted)]">Brak komentarzy.</p>
              )}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <textarea
              className="input-base flex-1 min-h-[40px]"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Dodaj komentarz przed spotkaniem…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submitComment();
              }}
            />
            <Button
              variant="secondary"
              loading={commentMutation.isPending}
              disabled={!commentText.trim()}
              onClick={submitComment}
            >
              Wyślij
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
